import {
  signinUser,
  ssoUser,
  refreshToken,
  logoutUser,
  sendOtp,
  verifyOtp,
  registerInterceptor,
  FetcherInterceptor,
  type SigninUserRequestBody,
  type SigninUserOkResponse,
  type SigninUserErrorResponse,
  type SsoUserRequestBody,
  type SsoUserOkResponse,
  type RefreshTokenRequestBody,
  type RefreshTokenOkResponse,
  type LogoutUserOkResponse,
  type SendOtpOkResponse,
  type SendOtpRequestBody,
  type SendOtpErrorResponse,
  type VerifyOtpRequestBody,
  type VerifyOtpErrorResponse
} from '@goldcast/api/auth';

import { hashOTP } from './utils';

import { getEnv } from '@/constants';
import { EVENT_APP_ORG_ID_KEY } from '@/constants/authKeys';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import uuid from '@/services/utils/uuid';

export interface AuthSignInResult {
  signInUserSession: {
    accessToken: {
      jwtToken: string;
    };
    idToken: {
      jwtToken: string;
    };
    refreshToken: {
      token: string;
    };
  };
  challengeParam?: {
    email: string;
  };
}

export type RequestOTPResponse = Required<SendOtpOkResponse>;

interface TokenResponseData {
  access_token: string;
  id_token: string;
  refresh_token: string;
  email?: string;
}

interface LogoutResponseData {
  status?: string;
}

export const GCAUTH_ACCESS_TOKEN_KEY = 'gcauth_access_token';
export const GCAUTH_REFRESH_TOKEN_KEY = 'gcauth_refresh_token';

const DEVICE_ID_KEY = 'gcauth_device_id';

function getDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = uuid();
    saveValueToLocalstore(DEVICE_ID_KEY, deviceId);
  }

  return deviceId;
}

function isRefreshTokenAlreadyUpdated(refreshTokenValue: string, storedRefreshToken: string): boolean {
  if (storedRefreshToken !== refreshTokenValue) {
    return true;
  }
  return false;
}

function isTokenExpired(jwtToken: string): boolean {
  try {
    const payload = jwtToken.split('.')[1];
    if (!payload) {
      return true;
    }

    const decoded = JSON.parse(atob(payload));
    const expiryTime = decoded.exp * 1000;
    const now = Date.now();

    if (!expiryTime) {
      return true;
    }
    return now >= expiryTime;
  } catch (error) {
    loggerService.error('Error decoding token', error);
    return true;
  }
}

registerInterceptor(FetcherInterceptor.PRE_PROCESS_OPTIONS, ({ options }) => {
  const baseUrl = getEnv('VUE_APP_GCAUTH_ENDPOINT') || '';
  const deviceId = getDeviceId();

  return {
    ...options,
    url: `${baseUrl}${options.url}`,
    headers: {
      ...options.headers,
      deviceId
    }
  };
});

class GCAuthService {
  private static instance: GCAuthService;
  private refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): GCAuthService {
    if (!GCAuthService.instance) {
      GCAuthService.instance = new GCAuthService();
    }
    return GCAuthService.instance;
  }

  private async refreshAccessToken(accessToken: string, refreshTokenValue: string): Promise<AuthSignInResult> {
    const organizationId = getValueFromLocalstore(EVENT_APP_ORG_ID_KEY);

    try {
      const requestPayload: RefreshTokenRequestBody = {
        refresh_token: refreshTokenValue,
        organization_id: organizationId
      };

      loggerService.log('[GCAuth] Attempting token refresh');

      const response: RefreshTokenOkResponse = await refreshToken({
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
        body: requestPayload
      });

      if (response.error) {
        loggerService.error('[GCAuth] Token refresh failed', response.error);
        throw new Error(response.message || response.error || 'Token refresh failed');
      }

      const data = response.data as TokenResponseData;

      if (!data?.access_token || !data?.id_token || !data?.refresh_token) {
        loggerService.error('[GCAuth] Token refresh failed - missing tokens');
        throw new Error('Token refresh failed - invalid response');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: data.access_token
          },
          idToken: {
            jwtToken: data.id_token
          },
          refreshToken: {
            token: data.refresh_token
          }
        }
      };

      loggerService.log('[GCAuth] Token refresh successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Token refresh error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Token refresh service unavailable');
    }
  }

  async getCurrentAccessToken(
    accessToken: string,
    refreshTokenValue: string,
    isUnauthorized: boolean,
    error: string
  ): Promise<{ accessToken: string; refreshToken: string }> {
    const storedAccessToken = getValueFromLocalstore(GCAUTH_ACCESS_TOKEN_KEY);
    const storedRefreshToken = getValueFromLocalstore(GCAUTH_REFRESH_TOKEN_KEY);

    if (!isUnauthorized) {
      loggerService.log('[GCAuth] Token still valid, using cached token');
      return { accessToken, refreshToken: refreshTokenValue };
    }

    loggerService.log('[GCAuth] Unauthorized error detected', error);

    if (this.refreshPromise) {
      loggerService.log('[GCAuth] Token refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      if (
        storedAccessToken &&
        storedRefreshToken &&
        isRefreshTokenAlreadyUpdated(refreshTokenValue, storedRefreshToken) &&
        !isTokenExpired(storedAccessToken)
      ) {
        loggerService.log('[GCAuth] Refresh token already updated, skipping refresh call');
        return { accessToken: storedAccessToken, refreshToken: storedRefreshToken };
      }

      loggerService.log('[GCAuth] Token is Unauthorized, refreshing automatically');
      const result = await this.refreshAccessToken(storedAccessToken, storedRefreshToken);
      return {
        accessToken: result.signInUserSession.accessToken.jwtToken,
        refreshToken: result.signInUserSession.refreshToken.token
      };
    })().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  async signIn(
    identifier: string,
    magicLinkSecret: string,
    eventId: string,
    eventShortId: string
  ): Promise<AuthSignInResult> {
    try {
      const requestPayload: SigninUserRequestBody = {
        identifier,
        magicLinkSecret,
        eventId,
        eventShortId
      };

      loggerService.log('[GCAuth] Attempting sign in');

      const response = (await signinUser({ body: requestPayload })) as SigninUserOkResponse & SigninUserErrorResponse;

      if (response?.error === 'session_exists') {
        loggerService.log('[GCAuth] Session exists - triggering OTP flow', response);
        throw Object.assign(new Error(response.message || 'Session exists'), {
          statusCode: 409,
          status: 409,
          error: response.error,
          details: response.details
        });
      }

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        loggerService.error('[GCAuth] Sign in failed - missing tokens or email', response);
        throw new Error('This link is invalid or broken. Please request a new link to continue.');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: response.access_token
          },
          idToken: {
            jwtToken: response.id_token
          },
          refreshToken: {
            token: response.refresh_token
          }
        },
        challengeParam: {
          email: response.email
        }
      };

      loggerService.log('[GCAuth] Sign in successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Sign in error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Authentication service unavailable');
    }
  }

  async ssoSignIn(code: string): Promise<SsoUserOkResponse> {
    try {
      const requestPayload: SsoUserRequestBody = {
        code,
        app: 'event'
      };

      loggerService.log('[GCAuth] Attempting SSO sign in');

      const response: SsoUserOkResponse = await ssoUser({ body: requestPayload });

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        loggerService.error('[GCAuth] SSO sign in failed - missing tokens or email', response);
        throw new Error('This link is invalid or broken. Please request a new link to continue.');
      }

      loggerService.log('[GCAuth] SSO sign in successful');
      return response;
    } catch (error) {
      loggerService.error('[GCAuth] SSO sign in error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('SSO authentication service unavailable');
    }
  }

  async requestOTP(
    emailId: string,
    eventId: string,
    eventShortId: string,
    magicLinkSecret: string
  ): Promise<RequestOTPResponse> {
    if (!emailId?.includes('@') || !eventId || !eventShortId || !magicLinkSecret) {
      throw new Error('Invalid request parameters');
    }

    try {
      loggerService.log('[GCAuth] Requesting OTP');

      const body: SendOtpRequestBody = {
        email_id: emailId,
        eventId,
        eventShortId: eventShortId.trim(),
        magicLinkSecret
      };
      const response = (await sendOtp({ body })) as SendOtpOkResponse & SendOtpErrorResponse;

      if (response?.error) {
        loggerService.error('[GCAuth] Failed to request OTP', response);
        throw Object.assign(new Error(response.message || response.error || 'Failed to request OTP'), {
          error: response.error,
          details: response.details
        });
      }

      if (!response.otp_id) {
        throw new Error('Invalid response from send_otp endpoint');
      }

      return response as RequestOTPResponse;
    } catch (error) {
      loggerService.error('[GCAuth] Request OTP error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Authentication service unavailable');
    }
  }

  async verifyOTP(otp: string, otpId: string): Promise<AuthSignInResult> {
    if (!otp || !otpId) {
      throw new Error('OTP and OTP ID are required');
    }

    try {
      loggerService.log('[GCAuth] Verifying OTP');

      const otpHash = await hashOTP(otp);
      const body: VerifyOtpRequestBody = { otp_hash: otpHash, otp_id: otpId };
      const response = (await verifyOtp({ body })) as TokenResponseData & VerifyOtpErrorResponse;

      if (response?.error) {
        loggerService.error('[GCAuth] Failed to verify OTP', response);
        throw Object.assign(new Error(response.message || response.error || 'Failed to verify OTP'), {
          error: response.error,
          details: response.details
        });
      }

      if (!response.access_token || !response.id_token || !response.refresh_token || !response.email) {
        throw new Error('Invalid response from verify_otp endpoint');
      }

      const authResult: AuthSignInResult = {
        signInUserSession: {
          accessToken: {
            jwtToken: response.access_token
          },
          idToken: {
            jwtToken: response.id_token
          },
          refreshToken: {
            token: response.refresh_token
          }
        },
        challengeParam: {
          email: response.email
        }
      };

      loggerService.log('[GCAuth] OTP verification successful');
      return authResult;
    } catch (error) {
      loggerService.error('[GCAuth] Verify OTP error', error);
      throw error instanceof Error ? error : new Error('Failed to verify OTP');
    }
  }

  async signOut(accessToken: string): Promise<boolean> {
    try {
      loggerService.log('[GCAuth] Attempting logout');

      const response: LogoutUserOkResponse = await logoutUser({
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (response.error) {
        loggerService.error('[GCAuth] Logout failed', response.error);
        throw new Error(response.message || response.error || 'Logout failed');
      }

      const data = response.data as LogoutResponseData;

      if (data?.status === 'logged_out' || response.success) {
        loggerService.log('[GCAuth] Logout successful');
        return true;
      }

      throw new Error('Unexpected logout response');
    } catch (error) {
      loggerService.error('[GCAuth] Logout error', error);

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Logout service unavailable');
    }
  }
}

export const GCAuth = GCAuthService.getInstance();
